import React, { useState, useRef, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {Link, useNavigate, useParams} from "react-router-dom";
import styled from "styled-components";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { updateAboutUsOurStory, getAboutUsOurStory, createAboutUsOurStory } from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";    
import FormGroup from "../../Common/FormGroup";

const validateImage = (file) => {
  if (!file) return "Image is required";
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return "Only JPEG, PNG, GIF, and WebP images are allowed";
  }
  return null;
};

// Validation schema
const validationSchema = Yup.object({
  name1: Yup.string()
    .required("Member 1 name is required"),
  position1: Yup.string()
    .required("Member 1 position is required"),
  name2: Yup.string()
    .required("Member 2 name is required"),
  position2: Yup.string()
    .required("Member 2 position is required"),
  image1: Yup.mixed()
    .required("Member 1 image is required"),
  image2: Yup.mixed()
    .required("Member 2 image is required"),
});

export default function OurStoryEdit() {
  const { id, partnerId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [image1, setImage1] = useState(null);
  const [image2, setImage2] = useState(null);
  const [imageErrors, setImageErrors] = useState({
    image1: null,                                      
    image2: null
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const [ourstory, setOurStory] = useState({
    name1: "",
    image1: "",
    position1: "",
    name2: "",
    image2: "",
    position2: "",
    index: null,
  });
  const inputRef1 = useRef(null);
  const inputRef2 = useRef(null);

  const handleImageChange1 = (event, setFieldValue) => {
    const file = event.target.files[0];
    
    if (!file) {
      setImage1(null);
      setFieldValue("image1", null);
      setImageErrors(prev => ({
        ...prev,
        image1: !Number(id) ? "Member 1 image is required" : null
      }));
      return;
    }
    
    const error = validateImage(file);
    
    if (!error) {
      setImage1(file);
      setFieldValue("image1", file);
      setImageErrors(prev => ({
        ...prev,
        image1: null // Clear any existing errors
      }));
    } else {
      setImage1(null);
      setFieldValue("image1", null);
      setImageErrors(prev => ({
        ...prev,
        image1: error
      }));
      // Reset the input
      event.target.value = '';
    }
  };

  const handleImageChange2 = (event, setFieldValue) => {
    const file = event.target.files[0];
    
    if (!file) {
      setImage2(null);
      setFieldValue("image2", null);
      setImageErrors(prev => ({
        ...prev,
        image2: !Number(id) ? "Member 2 image is required" : null
      }));
      return;
    }
    
    const error = validateImage(file);
    
    if (!error) {
      setImage2(file);
      setFieldValue("image2", file);
      setImageErrors(prev => ({
        ...prev,
        image2: null // Clear any existing errors
      }));
    } else {
      setImage2(null);
      setFieldValue("image2", null);
      setImageErrors(prev => ({
        ...prev,
        image2: error
      }));
      // Reset the input
      event.target.value = '';
    }
  };

  const fetchOurStoryData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await getAboutUsOurStory(id);
      const ourStoryData = res.data.data[0];
      console.log(res);
      setOurStory({
        ...ourStoryData,
        index: res.data.data.indexOf(ourStoryData),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOurStoryData();
  }, [id]);

  const validateImages = (values) => {
    const errors = {};
    
    // For new entries, both images are required
    if (!Number(id)) {
      if (!values.image1) {
        errors.image1 = "Member 1 image is required";
      }
      if (!values.image2) {
        errors.image2 = "Member 2 image is required";
      }
    }
    
    // Check for existing image errors (file type, size, etc.)
    if (imageErrors.image1 && !values.image1) {
      errors.image1 = imageErrors.image1;
    }
    if (imageErrors.image2 && !values.image2) {
      errors.image2 = imageErrors.image2;
    }
    
    return errors;
  };

  const handleSubmit = async (values) => {
    // Validate images
    const imageValidationErrors = validateImages(values);
    
    if (Object.keys(imageValidationErrors).length > 0) {
      setImageErrors(imageValidationErrors);
      showToast({
        type: 'error',
        message: "Please fix image validation errors before submitting.",
      });
      return;
    }
    
    setIsUpdating(true);
    const formData = new FormData();
    
    if (values.image1 instanceof File) {
      formData.append("image1", values.image1);
    }
    formData.append("name1", values.name1);
    formData.append("position1", values.position1);
    
    if (values.image2 instanceof File) {
      formData.append("image2", values.image2);
    }
    formData.append("name2", values.name2);
    formData.append("position2", values.position2);

    if (!Number(id)) {
      try {
        formData.append("partner_id", partnerId);
        const response = await createAboutUsOurStory(formData);
        if(response.status === 200){
          showToast({
            type: 'success',
            message: response?.data?.message || "Our story section has been created successfully!",
          });
          const ourStoryData = response.data.data;
          setOurStory(ourStoryData);
          if (ourStoryData && ourStoryData.id) {
            navigate(`/${partnerId}/aboutus/ourstory/edit/${ourStoryData.id}`);
          }
        }else{
          showToast({
            type: 'error',
            message: response?.data?.message || "Failed to create our story section. Please try again.",
          });
        }
        setIsUpdating(false);
        console.log(response.data);
      } catch (error) {
        showToast({
          type: 'error',
          message: error?.response?.data?.message || "Failed to create our story section. Please try again.",
        });
        console.error(error);
        setIsUpdating(false);
      }
    } else {
      try {
        const response = await updateAboutUsOurStory(id, formData);
        if(response.status === 200){
          showToast({ 
            type: 'success',
            message: response?.data?.message || "Our story section has been updated successfully!",
          });
        }else{
          showToast({
            type: 'error',
            message: response?.data?.message || "Failed to update our story section. Please try again.",
          });
        }
        setIsUpdating(false);
        console.log(response.data);
      } catch (error) {
        showToast({ 
          type: 'error',
          message: error?.response?.data?.message || "Failed to update our story section. Please try again.",
        });
        console.error(error);
        setIsUpdating(false);
      }
    }
  };

  const initialValues = {
    name1: ourstory.name1 || "",
    position1: ourstory.position1 || "",
    name2: ourstory.name2 || "",
    position2: ourstory.position2 || "",
    image1: ourstory.image1 || "",
    image2: ourstory.image2 || "",
    old_image1: ourstory.image1 || "",
    old_image2: ourstory.image2 || "",
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress/> : 
        <FormWrapper>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ handleSubmit, handleChange,setFieldValue, values, errors, touched }) => (
              <Form  onSubmit={handleSubmit} noValidate>
                {console.log('--> values', values)}
                <Title>{Number(id) ? 'Edit' : 'Add' } Our Story section</Title>
                <SliderDiv>
                  <SubTitle>{Number(id) ? 'Edit' : 'Add' } Members</SubTitle>
                  
                  {/* Member 1 */}
                  <Row>
                      <FormGroup requiredField controlId="OurStoryControl" label='Member 01' error={touched.image1 && errors.image1}> 
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            handleImageChange1(e, setFieldValue);
                          }}
                          style={{ display: "none" }}
                          ref={inputRef1}
                          name="image1"
                        />
                        {values.image1 ? (
                          <ImageWrapper>
                            <UploadImage>
                              <BootstrapImage
                                src={values.image1 instanceof File ? URL.createObjectURL(values.image1) : values.image1 }
                                alt="image1"
                                onClick={() => {
                                  inputRef1.current.click();
                                }}
                              />
                            </UploadImage>
                            <br />
                            {(values.image1 instanceof File || (values.image1 && values.image1 !== values.old_image1)) && (
                            <RemoveButtonWrapper>
                              <BootstrapButton
                                variant="outline-danger"
                                onClick={() => {
                                  setFieldValue("image1", values.old_image1);
                                  if (inputRef1.current) {
                                    inputRef1.current.value = '';
                                  }
                                }}
                              >
                                <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                              </BootstrapButton>
                            </RemoveButtonWrapper>
                            )}
                          </ImageWrapper>
                        ) : (
                          Number(id) && (values.image1 || values.old_image1 ) ? (
                            <UploadImage>
                              <BootstrapImage
                                src={values.image1 || values.old_image1}
                                alt="image1"
                                onClick={() => {
                                inputRef1.current.click();
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.opacity = 0.8;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.opacity = 1;
                              }}
                            />
                            </UploadImage>
                          ) : (
                            <Button 
                            className="mb-0"
                              onClick={() => {
                                inputRef1.current.click();
                              }}
                              hasError={imageErrors.image1}
                            > 
                              Upload the image 
                            </Button>
                          )
                        )}
                      </FormGroup>
                    <Col md={8} style={{ marginTop: "2.5vw" }}>
                      <Row>
                        <FormGroup
                        requiredField
                          label='Name'
                          controlId="ourstory-name1"
                          error={errors.name1}
                        >
                          <Form.Control
                            type="text"
                            name="name1"
                            value={values.name1}
                            onChange={handleChange}
                            isInvalid={touched.name1 && errors.name1}
                          />
                        </FormGroup>

                        <FormGroup
                          label='Position'
                          controlId="ourstory-position1"
                          requiredField
                          error={errors.position1}
                        >
                          <Form.Control
                            type="text"
                            name="position1"
                            value={values.position1}
                            onChange={handleChange}
                            isInvalid={touched.position1 && errors.position1}
                          />
                        </FormGroup>
                      </Row>
                    </Col>
                  </Row>

                  {/* Member 2 */}
                  <Row>
                    <Col md={4}>
                      <FormGroup requiredField controlId="OurStoryControl2" label='Member 02' error={touched.image2 && errors.image2}>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            handleImageChange2(e, setFieldValue);
                          }}
                          style={{ display: "none" }}
                          ref={inputRef2}
                          name="image2"
                        />
                        {values.image2 ? (
                          <ImageWrapper>
                            <UploadImage>
                              <BootstrapImage
                                style={{ maxWidth: "13vw" }}
                                src={values.image2 instanceof File ? URL.createObjectURL(values.image2) : values.image2 }
                                alt="image2"
                                onClick={() => {
                                  inputRef2.current.click();
                                }}
                              />
                            </UploadImage>
                            <br />
                            {(values.image2 instanceof File || (values.image2 && values.image2 !== values.old_image2)) && (
                            <RemoveButtonWrapper>
                              <BootstrapButton
                                variant="outline-danger"
                                onClick={() => {
                                  setFieldValue("image2", values.old_image2);
                                  if (inputRef2.current) {
                                    inputRef2.current.value = '';
                                  }
                                }}
                              >
                                <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                              </BootstrapButton>
                            </RemoveButtonWrapper>
                            )}
                          </ImageWrapper>
                        ) : (
                          Number(id) && (values.image2 || values.old_image2) ? (
                            <UploadImage>
                              <BootstrapImage
                                src={values.image2 || values.old_image2}
                                alt="image2"
                                onClick={() => {
                                inputRef2.current.click();
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.opacity = 0.8;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.opacity = 1;
                              }}
                              />
                            </UploadImage>
                          ) : (
                            <Button 
                            className="mb-0"
                              onClick={() => {
                                inputRef2.current.click();
                              }}
                              hasError={imageErrors.image2}
                            > 
                              Upload the image 
                            </Button>
                          )
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={8} style={{ marginTop: "2.5vw" }}>
                      <Row>
                        <FormGroup
                          label='Name'
                          controlId="ourstory-name2"
                          error={errors.name2}
                          requiredField
                        >
                          <Form.Control
                            type="text"
                            name="name2"
                            value={values.name2}
                            onChange={handleChange}
                            isInvalid={touched.name2 && errors.name2}
                          />
                        </FormGroup>

                        <FormGroup
                          label='Position'
                          controlId="ourstory-position2"
                          error={errors.position2}
                          requiredField
                        >
                          <Form.Control
                            type="text"
                            name="position2"
                            value={values.position2}
                            onChange={handleChange}
                            isInvalid={touched.position2 && errors.position2}
                          />
                        </FormGroup>
                      </Row>
                    </Col>
                  </Row>

                  {/* Button Section */}
                  <ButtonWrapper className="mt-3">
                    <Link
                      to={Number(id) ? `/${partnerId}/aboutus/ourstory/view/${id}` : `/${partnerId}/aboutus`}
                      style={{
                        width: "50%",
                      }}
                    >
                      <Button variant="secondary">Go Back</Button>
                    </Link>
                    <Button
                      type="submit"
                      disabled={isUpdating}
                      style={{
                        width: "50%",
                      }}
                    >
                      {isUpdating ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </SliderDiv>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      }
    </>
  );
}

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
   @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h6``;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  gap: 2rem;
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
    top: -17px;
    right: -14px;
    @media (max-width:576px) {
     top: -14px;
    right: -6px;
`;

const ImageWrapper = styled.div`
    position:relative;
    max-width: 13vw;
   @media (max-width:576px) {
   max-width: 100vw;
  }
`;

const UploadImage = styled.div`
  max-width: 13vw;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
   max-width: 100vw;
  }
`;

const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "13vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "95vw",
  },
}));

const BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));
